import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToolbarModule } from 'primeng/toolbar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ComplaintTypeService, ComplaintTypeModel } from '../../services/complaint-type.service';

@Component({
  selector: 'app-complaint-types',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CardModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToolbarModule,
    ConfirmDialogModule,
    ToastModule,
    DialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <p-card>
      <ng-template pTemplate="header">
        <p-toolbar>
          <ng-template pTemplate="start">
            <h2>Tipos de Denúncia</h2>
          </ng-template>
          <ng-template pTemplate="end">
            <p-button
              label="Recarregar"
              icon="pi pi-refresh"
              (onClick)="load()"
              [outlined]="true"
              severity="secondary"
              size="small"
              class="mr-2">
            </p-button>
            <p-button
              label="Novo Tipo"
              icon="pi pi-plus"
              (onClick)="openDialog()"
              severity="primary">
            </p-button>
          </ng-template>
        </p-toolbar>
      </ng-template>

      <ng-template pTemplate="content">
        <!-- Filtro -->
        <div class="filters-container p-3">
          <span class="p-input-icon-left">
            <i class="pi pi-search"></i>
            <input
              pInputText
              type="text"
              placeholder="Buscar tipo..."
              [(ngModel)]="searchTerm"
              (input)="applyFilter()"
              style="min-width: 280px;" />
          </span>
        </div>

        <!-- Tabela -->
        <p-table
          [value]="filtered"
          [paginator]="true"
          [rows]="10"
          [rowsPerPageOptions]="[5, 10, 20]"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="{first} - {last} de {totalRecords} registros"
          responsiveLayout="scroll"
          [loading]="isLoading">

          <ng-template pTemplate="header">
            <tr>
              <th pSortableColumn="id" style="width:80px">ID <p-sortIcon field="id"></p-sortIcon></th>
              <th pSortableColumn="description">Descrição <p-sortIcon field="description"></p-sortIcon></th>
              <th style="width:160px">Ações</th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-type>
            <tr>
              <td>{{ type.id }}</td>
              <td>{{ type.description }}</td>
              <td>
                <div class="action-buttons">
                  <p-button
                    icon="pi pi-pencil"
                    title="Editar"
                    (onClick)="openDialog(type)"
                    severity="info"
                    [outlined]="true"
                    size="small">
                  </p-button>
                  <p-button
                    icon="pi pi-trash"
                    title="Excluir"
                    (onClick)="confirmDelete(type)"
                    severity="danger"
                    [outlined]="true"
                    size="small">
                  </p-button>
                </div>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="3" class="text-center p-4">
                <i class="pi pi-info-circle" style="font-size:2rem; color:#aaa"></i>
                <p style="color:#666; margin-top:8px">Nenhum tipo encontrado</p>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </ng-template>
    </p-card>

    <!-- Dialog de cadastro/edição -->
    <p-dialog
      [(visible)]="dialogVisible"
      [header]="editingId ? 'Editar Tipo' : 'Novo Tipo'"
      [modal]="true"
      [style]="{ width: '400px' }"
      [closable]="true"
      (onHide)="resetForm()">

      <form [formGroup]="form" (ngSubmit)="save()" class="dialog-form">
        <div class="field">
          <label for="description">Descrição <span class="required">*</span></label>
          <input
            id="description"
            pInputText
            formControlName="description"
            placeholder="Informe a descrição"
            class="w-full"
            [class.ng-invalid]="form.get('description')?.invalid && form.get('description')?.touched" />
          <small *ngIf="form.get('description')?.invalid && form.get('description')?.touched" class="error-msg">
            Descrição é obrigatória (mínimo 2 caracteres).
          </small>
        </div>
      </form>

      <ng-template pTemplate="footer">
        <p-button
          label="Cancelar"
          icon="pi pi-times"
          (onClick)="dialogVisible = false"
          [outlined]="true"
          severity="secondary">
        </p-button>
        <p-button
          [label]="editingId ? 'Salvar' : 'Cadastrar'"
          icon="pi pi-check"
          (onClick)="save()"
          [loading]="isSaving"
          [disabled]="form.invalid">
        </p-button>
      </ng-template>
    </p-dialog>

   <p-confirmDialog
      [acceptLabel]="'Sim'"
      [rejectLabel]="'Não'"
      acceptButtonStyleClass="p-button-success"
      rejectButtonStyleClass="p-button-secondary"
    >
    </p-confirmDialog>
    <p-toast></p-toast>
  `,
  styles: [`
    .filters-container {
      border-bottom: 1px solid #e0e0e0;
    }
    .action-buttons {
      display: flex;
      gap: 0.5rem;
    }
    .dialog-form {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: 0.5rem 0;
    }
    .field {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
    .field label { font-weight: 500; font-size: 0.9rem; }
    .required { color: #e53935; }
    .error-msg { color: #e53935; font-size: 0.8rem; }
    :host ::ng-deep .p-toolbar { border: none; padding: 1rem 0; }
    :host ::ng-deep .p-card .p-card-content { padding: 0; }
    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td {
      padding: 0.5rem;
      vertical-align: middle;
    }
  `]
})
export class ComplaintTypesComponent implements OnInit {
  types: ComplaintTypeModel[] = [];
  filtered: ComplaintTypeModel[] = [];
  searchTerm = '';
  isLoading = false;
  isSaving = false;
  dialogVisible = false;
  editingId: number | null = null;

  form = new FormGroup({
    description: new FormControl('', [Validators.required, Validators.minLength(2)])
  });

  constructor(
    private service: ComplaintTypeService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.service.findAll().subscribe({
      next: (data) => {
        this.types = data;
        this.applyFilter();
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: `Erro ao carregar tipos. (HTTP ${err.status})` });
        this.isLoading = false;
      }
    });
  }

  applyFilter(): void {
    const term = this.searchTerm.toLowerCase().trim();
    this.filtered = term
      ? this.types.filter(t => (t.description ?? '').toLowerCase().includes(term) || String(t.id ?? '').includes(term))
      : [...this.types];
  }

  openDialog(type?: ComplaintTypeModel): void {
    this.editingId = type?.id ?? null;
    this.form.reset({ description: type?.description ?? '' });
    this.dialogVisible = true;
  }

  resetForm(): void {
    this.editingId = null;
    this.form.reset();
  }

  save(): void {
    if (this.form.invalid) return;
    this.isSaving = true;
    const payload: ComplaintTypeModel = { description: this.form.value.description ?? '' };

    const request$ = this.editingId
      ? this.service.update(this.editingId, { ...payload, id: this.editingId })
      : this.service.save(payload);

    request$.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: this.editingId ? 'Tipo atualizado.' : 'Tipo cadastrado.'
        });
        this.dialogVisible = false;
        this.isSaving = false;
        this.load();
      },
      error: (err) => {
        console.error(err);
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: err.error?.message ?? 'Erro ao salvar.' });
        this.isSaving = false;
      }
    });
  }

  confirmDelete(type: ComplaintTypeModel): void {
    this.confirmationService.confirm({
      message: `Deseja excluir o tipo "${type.description}"?`,
      header: 'Confirmar exclusão',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.delete(type.id!)
    });
  }

  private delete(id: number): void {
    this.service.softDeleteById(id).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Tipo excluído.' });
        this.load();
      },
      error: (err) => {
        console.error(err);
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: err.error?.message ?? 'Erro ao excluir.' });
      }
    });
  }
}
